import React, { useState, useCallback, useEffect, useRef } from "react";
import { Col } from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import { useFormikContext } from "formik";

import { useDropzone } from "react-dropzone";

import { MdOutlineUploadFile } from "react-icons/md";

import { uploadSlice } from "../../store/slices/upload-slice";
import { setUploadFile } from "../../store/slices/upload-slice";
// import { selectUploadSlice } from '../../store/slices/upload-slice';

import { axiosInstance } from "../../utils/axios-instance";
import { PDFDocument } from "pdf-lib";
import { fetchProfile } from "../../store/thunks/fetch-profile-thunk";
import { postProfile } from "../../store/thunks/post-profile-thunk";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// import { useSelector } from 'react-redux';
import { useDispatch } from "react-redux";

import prettyBytes from "pretty-bytes";
import { original } from "@reduxjs/toolkit";

export const FileUpload = ({ containerName }) => {
    const { isSubmitting, status, isValid } = useFormikContext();
    const ToastID = React.useRef(null);

    const dispatch = useDispatch();
    // List of files inside of the container
    const [myFiles, setMyFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setShowErrorMessage] = useState("");
    const [pdfIsEncrypted, setPdfIsEncrypted] = useState(false);

    // Hook that updates the list of files being uploaded when new files are added
    const onDrop = useCallback(
        (acceptedFiles) => {
            acceptedFiles.forEach((file) => {
                // check if pdf is encrypted
                if (file.type === "application/pdf") {
                    const reader = new FileReader();

                    reader.onload = async () => {
                        const binaryStr = reader.result;

                        let originalPdf = await PDFDocument.load(binaryStr, {
                            ignoreEncryption: true,
                        });
                        if (originalPdf.isEncrypted === true) {
                            setPdfIsEncrypted(true);
                        } else {
                            setShowError(false);
                            setMyFiles([...acceptedFiles]);
                        }
                    };

                    reader.readAsArrayBuffer(file);
                } else {
                    setShowError(false);
                    setMyFiles([...acceptedFiles]);
                }
            });
        },
        [myFiles],
    );

    // useEffect(() => {
    //     dispatch(setUploadFile(myFiles));
    // }, [myFiles]);

    /**
     * Function that gets a file and checks if that file is already added to the list to upload.
     * @param {*} file being uploaded.
     * @returns {object} if the file already exists in the container.
     */
    const duplicateValidator = (file) => {
        let isDuplicate = false;
        let isFileTooBig = false;

        // reset
        setPdfIsEncrypted(false);

        // Loop through the files in the container and check if the file being added already exists
        myFiles.map((f) => {
            if (f.path === file.path) {
                isDuplicate = true;
            }
        });

        if (file.size > 5242880) {
            isFileTooBig = true;
        }

        if (isDuplicate) {
            return {
                code: "file-already-added",
                message: `${file.path} is already staged`,
            };
        }

        if (isFileTooBig) {
            return {
                code: "file-too-large",
                message: `File is too large. Over 5MB limit`,
            };
        }

        return null;
    };

    // Initialize the react dropzone with setting to allow a custom validator and only accept certain file types.
    const { getRootProps, getInputProps, acceptedFiles, fileRejections } =
        useDropzone({
            onDrop,
            accept: {
                "application/pdf": [".pdf"],
                "image/*": [".png", ".jpeg", ".jpg"],
            },
            validator: duplicateValidator,
            maxFiles: 1,
        });

    /**
     * Function that removes all files in the container.
     */
    const clearAllFiles = () => {
        setMyFiles([]);
    };

    /**
     * Function that creates formdata from the files provided and sends it to
     * backend.
     */
    const uploadAllFiles = async () => {
        const fd = new FormData();
        myFiles.forEach((file) => {
            // console.log(file);
            fd.append("upload", file);
        });

        fd.append("formName", containerName);

        // for (var [key, value] of fd.entries()) {
        //     // console.log(key, value);
        // }

        let res;
        setLoading(true);
        try {
            res = await axiosInstance({
                method: "post",
                url: "/upload",
                data: fd,
            });
            toast.update(ToastID.current, {
                render: `Successfully uploaded: ${myFiles[0].name}`,
                type: "success",
                isLoading: false,
                theme: "colored",
                autoClose: "3500",
                containerId: "root",
            });

            clearAllFiles();
            setLoading(false);
            setSuccess(true);
            // setTimeout(() => {
            //     // setSuccess(false)
            //     dispatch(fetchProfile())
            // }, 2000)
            return Promise.resolve();
        } catch (error) {
            // console.log(error);
            const errorCode = error?.request?.status;

            if (errorCode === 415) {
                setShowError(true);
                setShowErrorMessage(
                    `Upload failed: '${myFiles[0].name}' is encrypted.`,
                );
                toast.update(ToastID.current, {
                    render: `'${myFiles[0].name}' Upload failed: PDF documents cannot be encrypted.`,
                    type: "error",
                    isLoading: false,
                    theme: "colored",
                    closeButton: true,
                    containerId: "root",
                });
                console.log(fileRejections);
                fileRejections.push({
                    file: myFiles[0],
                    errors: [
                        {
                            code: "pdf_document_encrypted",
                            message: "File is encrypted.",
                        },
                    ],
                });
            } else {
                toast.update(ToastID.current, {
                    render: `Upload failed: ${myFiles[0].name}`,
                    type: "error",
                    isLoading: false,
                    theme: "colored",
                    closeButton: true,
                    containerId: "root",
                });
            }

            setLoading(false);
            return Promise.reject(error);
            // return Promise.reject('bad upload')
        }
    };

    /**
     * Function that removes file i inside the container.
     * @param {i} file the file being removed.
     */
    const removeFile = (file) => () => {
        // Create a temporary deep copy array of the current files in the list.
        let tempFileList = [...myFiles];
        // Removed the file that is being removed.
        tempFileList.splice(tempFileList.indexOf(file), 1);
        // Set the files in the container to be the updated temporary array.
        setMyFiles(tempFileList);
        setShowError(false);
    };

    const fileRejectionItems = fileRejections.map(({ file, errors }) => (
        <Card.Body key={file.path} className="border-3 border-warning">
            <Card.Footer>
                <h4 className="d-flex justify-content-center text-danger">
                    File Rejected:
                </h4>
                <Row>
                    <Col className="d-flex justify-content-center">
                        <b>File:&nbsp;</b>&nbsp;{file.path}
                    </Col>
                </Row>
                <Row>
                    <Col className="d-flex justify-content-center mb-1">
                        <b>Size:&nbsp;</b>
                        {prettyBytes(file.size)}
                    </Col>
                </Row>

                <ul>
                    {errors.map((e) => (
                        <li
                            className="text-danger d-flex justify-content-center"
                            key={e.code}
                        >
                            {e.message}
                        </li>
                    ))}
                </ul>
            </Card.Footer>
        </Card.Body>
        // <li key={file.path}>
        //   {file.path} - {file.size} bytes
        //   <ul>
        //     {errors.map(e => (
        //       <li key={e.code}>{e.message}</li>
        //     ))}
        //   </ul>
        // </li>
    ));

    // id.current = toast(id.current, {render: `Upload Pending: `, type: "loading", isLoading: true})
    /**
     * Get a list of the files in the container within html and add the button to remove it.
     */
    const listOfFiles = myFiles.map((file, idx) => {
        // console.log(file);
        const lastModifiedDate = new Date(file.lastModified).toDateString();
        return (
            <Card key={idx}>
                <Card.Body>
                    <Card.Title>
                        <Col className="d-flex justify-content-start">
                            <b>{file.path}</b>
                        </Col>
                    </Card.Title>

                    <Card.Body>
                        <Row>
                            <Col className="d-flex justify-content-start">
                                <b>Modified Last:&nbsp;</b>&nbsp;
                                {lastModifiedDate}
                            </Col>
                        </Row>
                        <Row>
                            <Col className="d-flex justify-content-start mb-1">
                                <b>Size:&nbsp;</b>
                                {prettyBytes(file.size)}
                            </Col>
                        </Row>
                        <Row>
                            <Button
                                className="btn btn-danger"
                                onClick={removeFile(file)}
                            >
                                Cancel Upload
                            </Button>
                        </Row>
                    </Card.Body>
                </Card.Body>
            </Card>
        );
    });

    // useEffect(()=> {
    //     // If form passed validation and submitted
    //     // AND theres a container name
    //     // AND theres files in the queue
    //     // upload them to the container
    //     if(isSubmitting && containerName && myFiles.length > 0) {
    //         uploadAllFiles()
    //     }
    // }, [isSubmitting, myFiles])

    useEffect(() => {
        // If form passed validation and submitted
        // AND theres a container name
        // AND theres files in the queue
        // upload them to the container
        // console.log(isSubmitting && containerName && myFiles.length > 0)

        if (isValid && isSubmitting && containerName && myFiles.length > 0) {
            // console.log('Creating ' + containerName + " promise")

            ToastID.current = toast.loading(
                `Upload Pending: ${myFiles[0].name}`,
                {
                    theme: "colored",
                    containerId: "root",
                    position: "bottom-right",
                },
            );
            let uploadPromise = new Promise((resolve, reject) => {
                uploadAllFiles()
                    .then(() => {
                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
            dispatch(setUploadFile(uploadPromise));
        }
    }, [isValid, isSubmitting, myFiles]);

    if (!containerName) {
        return;
    }

    // toast('hy')
    // React dropzone
    return (
        <>
            <Row className="bg-white rounded-3 mx-auto shadow-lg border-opacity-10 mt-2 mb-3 border border-5">
                <Col xl={6} xxl={8} className="text-center mx-auto py-3">
                    <div {...getRootProps()}>
                        <input type="file" name="data" {...getInputProps()} />
                        {listOfFiles.length === 0 && (
                            <>
                                {/* {isDragActive ? ( */}
                                <MdOutlineUploadFile className="file-upload-icon text-center" />
                                {/* ) : (
                                    <MdOutlineUploadFile className="file-upload-icon text-center" />
                                )} */}
                                <Row className="justify-content-center">
                                    <Col
                                        xl={"auto"}
                                        xxl={"auto"}
                                        className="mx-0 px-0"
                                    >
                                        <h4 className="text-primary mx-0 px-0">
                                            Choose a file
                                        </h4>
                                    </Col>
                                    <Col
                                        xl={"auto"}
                                        xxl={"auto"}
                                        className="mx-0 "
                                    >
                                        <h4 className="mx-0 px-0">
                                            or drag it here
                                        </h4>
                                    </Col>
                                </Row>
                                {/* <Row className="justify-content-center">
                                    <Col xl={"auto"} xxl={"auto"} className="mx-0 px-0">
                                        <h4 className="text-danger mx-0 px-0">
                                            .png, .jpeg, .jpg files types only
                                        </h4>
                                    </Col>
                                </Row> */}
                            </>
                        )}
                    </div>

                    <Row>{listOfFiles}</Row>
                    <Row>
                        <Col>
                            {loading && (
                                <Spinner
                                    className="d-flex justify-content-center"
                                    animation="border"
                                    role="status"
                                >
                                    <span className="visually-hidden">
                                        Loading...
                                    </span>
                                </Spinner>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        {success && (
                            <h5 className="text-success">
                                File successfully uploaded
                            </h5>
                        )}
                    </Row>
                    <Row>
                        {showError && (
                            <h5 className="text-danger">{errorMessage}</h5>
                        )}
                    </Row>
                    {/* {listOfFiles.length > 0 && (
                        <button type="button" onClick={clearAllFiles}>
                            Clear
                        </button>
                    )} */}
                    {/* {listOfFiles.length > 0 && (
                        <Button className="mt-3 btn btn-success" onClick={uploadAllFiles}>
                            Upload File
                        </Button>
                    )} */}
                </Col>
            </Row>
            {fileRejectionItems}
            {listOfFiles.length > 0 && !showError && (
                <Row className="d-flex justify-content-center mx-auto ">
                    <Col
                        xl={"auto"}
                        xxl={"auto"}
                        className="d-flex justify-content-center"
                    >
                        <h4 className="d-flex justify-content-center text-success">
                            File: &apos;{myFiles[0].name}&apos; Ready for
                            Upload.
                        </h4>
                    </Col>
                </Row>
            )}
            {pdfIsEncrypted && (
                <>
                    <Row className="d-flex justify-content-center mx-auto ">
                        <Col
                            xl={"auto"}
                            xxl={"auto"}
                            className="d-flex justify-content-center"
                        >
                            <h5 className="text-danger">
                                FILE UPLOAD FAILED. Encrypted PDFs are not
                                allowed. Please Decrypt the pdf and try again.
                            </h5>
                        </Col>
                    </Row>
                    <br />
                </>
            )}
            <Row className="d-flex justify-content-center mx-auto ">
                <Col
                    xl={"auto"}
                    xxl={"auto"}
                    className="d-flex justify-content-center"
                >
                    <h5>1. Files must be under 5MB in size.</h5>
                </Col>
            </Row>
            <Row className="d-flex justify-content-center mx-auto ">
                <Col
                    xl={"auto"}
                    xxl={"auto"}
                    className="d-flex justify-content-center"
                >
                    <Row>
                        <h5>2. File types allowed: .pdf, .png, .jpeg, .jpg.</h5>
                    </Row>
                </Col>
            </Row>
            <Row className="d-flex justify-content-center mx-auto ">
                <Col
                    xl={"auto"}
                    xxl={"auto"}
                    className="d-flex justify-content-center"
                >
                    <h5>3. PDF documents cannot be encrypted.</h5>
                </Col>
            </Row>
        </>
    );
};
